/**
 * Refiner Prompts (Content Excellence System)
 *
 * Prompts for extracting insights and polishing expression.
 *
 * @module config/prompts/refiner-prompts
 */
// ═══════════════════════════════════════════════════════════════════════════
// REFINER PROMPTS
// ═══════════════════════════════════════════════════════════════════════════
export const REFINER_EXTRACT_INSIGHT = {
    id: 'REFINER_EXTRACT_INSIGHT',
    name: 'Refiner Insight Extraction',
    description: 'Extract valuable insights from raw content',
    template: `You are extracting valuable insights from content that may have poor writing.

Focus on finding:
1. Original ideas or perspectives
2. Genuine observations or realizations
3. Meaningful connections or patterns
4. Authentic emotional truths

For each insight, provide:
- coreIdea: The essential idea (1-2 sentences)
- originalPhrasing: How it's written in the source
- location: Where in the text (beginning/middle/end or brief quote)
- confidence: How confident you are this is a real insight (0-1)
- context: Any context needed to understand it

{{#if knownInsights}}
These insights have already been identified - verify and expand:
{{#each knownInsights}}
- "{{this.insight}}" at {{this.location}}
{{/each}}
{{/if}}

Content:
{{content}}

Respond with JSON:
{
  "insights": [
    {
      "coreIdea": "string",
      "originalPhrasing": "string",
      "location": "string",
      "confidence": 0.0-1.0,
      "context": "string or null"
    }
  ]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['refiner'],
};
export const REFINER_VERIFY_PRESERVATION = {
    id: 'REFINER_VERIFY_PRESERVATION',
    name: 'Refiner Preservation Verification',
    description: 'Verify that refinement preserved original insights',
    template: `You are verifying that a text refinement preserved the original insights.

For each insight listed, determine:
1. Is it preserved in the refined text?
2. Is it distorted or changed in meaning?
3. Is it lost entirely?

Also identify any new concerns:
- Meaning shifts
- Tone changes that alter interpretation
- Missing context that was important

Original Text:
{{original}}

Refined Text:
{{refined}}

Insights to verify:
{{#each insights}}
- ID: {{this.id}} | Insight: {{this.coreIdea}}
{{/each}}

Respond with JSON:
{
  "score": 0.0-1.0,
  "preservedInsights": ["insight IDs that were preserved"],
  "lostInsights": ["insight IDs that were lost or distorted"],
  "issues": ["specific problems found"],
  "suggestions": ["how to improve preservation"]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['refiner'],
};
export const REFINER_POLISH_EXPRESSION = {
    id: 'REFINER_POLISH_EXPRESSION',
    name: 'Refiner Expression Polishing',
    description: 'Polish a piece of writing while preserving its core insight',
    template: `You are polishing a piece of writing to better express its core insight.

CORE INSIGHT TO PRESERVE:
{{coreIdea}}

{{#if context}}CONTEXT: {{context}}{{/if}}

RULES:
1. PRESERVE the exact meaning - do not add or remove ideas
2. Improve clarity and flow
3. Remove unnecessary words
4. Make it memorable if possible
5. Keep the authentic voice where present

DO NOT:
- Add new ideas not in the original
- Change the perspective or stance
- Make it sound generic or corporate
- Add hedging or qualifications not present

Original Text:
{{originalText}}

Respond with JSON:
{
  "polishedText": "the refined text",
  "changesSummary": "brief description of changes made",
  "preservationConfidence": 0.0-1.0
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.5,
        maxTokens: 2048,
    },
    version: 1,
    usedBy: ['refiner'],
};
// ═══════════════════════════════════════════════════════════════════════════
// INLINE MIGRATION PROMPTS (formerly hardcoded in refiner)
// ═══════════════════════════════════════════════════════════════════════════
export const REFINER_INLINE_VERIFY = {
    id: 'REFINER_INLINE_VERIFY',
    name: 'Refiner Inline Preservation Verification',
    description: 'Preservation verification (migrated from refiner.ts inline)',
    template: `You are verifying that a text refinement preserved the original insights.

For each insight listed, determine:
1. Is it preserved in the refined text?
2. Is it distorted or changed in meaning?
3. Is it lost entirely?

Also identify any new concerns:
- Meaning shifts
- Tone changes that alter interpretation
- Missing context that was important

Respond with JSON:
{
  "score": 0.0-1.0,
  "preservedInsights": ["insight IDs that were preserved"],
  "lostInsights": ["insight IDs that were lost or distorted"],
  "issues": ["specific problems found"],
  "suggestions": ["how to improve preservation"]
}`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 1024,
    },
    version: 1,
    usedBy: ['refiner'],
};
export const REFINER_INLINE_QUICK_VERIFY = {
    id: 'REFINER_INLINE_QUICK_VERIFY',
    name: 'Refiner Inline Quick Verification',
    description: 'Quick preservation score (migrated from refiner.ts inline)',
    template: `Rate how well the refined text preserves the core insight from the original.
Score from 0.0 (completely lost) to 1.0 (perfectly preserved).
Respond with JSON: { "score": 0.0-1.0, "reason": "brief explanation" }`,
    requirements: {
        capabilities: ['json-mode'],
        temperature: 0.3,
        maxTokens: 256,
    },
    version: 1,
    usedBy: ['refiner'],
};
export const REFINER_PROMPTS = [
    REFINER_EXTRACT_INSIGHT,
    REFINER_VERIFY_PRESERVATION,
    REFINER_POLISH_EXPRESSION,
    REFINER_INLINE_VERIFY,
    REFINER_INLINE_QUICK_VERIFY,
];
//# sourceMappingURL=refiner-prompts.js.map